/**
 * Dining-day (meal_date / order_date) helpers.
 *
 * Cherish at Central Park runs in `America/Vancouver` (PT). All meal-date
 * bucketing uses the dining room's local calendar day — never UTC —
 * because a late dinner order at 19:00 PT is 02:00 UTC the next day and
 * `new Date().toISOString().split('T')[0]` silently rolls those orders
 * into tomorrow's bucket. Same risk on tablets with skewed wallclocks.
 *
 * Override via `NEXT_PUBLIC_DINING_TIMEZONE` for community relocations
 * or test fixtures. The `en-CA` locale produces YYYY-MM-DD natively,
 * so the formatter output is already in the storage format used by
 * Appwrite (meal_date / order_date are string fields).
 *
 * Audit ref: BACKEND_PRACTICES_2026-05-12 P1 #20 (server-side meal_date
 * resolution) — this helper closes the issue from both ends, since
 * `Intl.DateTimeFormat` works identically in Node and the browser.
 *
 * Use this everywhere meal_date or order_date is written or read.
 * Do not write `new Date().toISOString().split('T')[0]` again.
 */

const DEFAULT_DINING_TIMEZONE = 'America/Vancouver'

function resolveTimezone(): string {
  // Read at call time so test fixtures can set process.env before import.
  return (
    (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_DINING_TIMEZONE) ||
    DEFAULT_DINING_TIMEZONE
  )
}

let cachedTz: string | null = null
let cachedFormatter: Intl.DateTimeFormat | null = null

function getFormatter(): Intl.DateTimeFormat {
  const tz = resolveTimezone()
  if (!cachedFormatter || cachedTz !== tz) {
    cachedTz = tz
    cachedFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }
  return cachedFormatter
}

/**
 * Today's dining-day string in YYYY-MM-DD, evaluated in the dining
 * room's local timezone.
 */
export function getMealDateToday(): string {
  return getFormatter().format(new Date())
}

/**
 * Convert a Date to its YYYY-MM-DD dining-day string. Use this when the
 * input is a timestamp (e.g. `ordered_at`) and you need its meal_date.
 */
export function getMealDate(date: Date): string {
  return getFormatter().format(date)
}

/**
 * The dining timezone currently in effect. Exposed for diagnostics /
 * reporting headers, not for date math — use `getMealDateToday()` for
 * dating writes.
 */
export function getDiningTimezone(): string {
  return resolveTimezone()
}

let cachedHourTz: string | null = null
let cachedHourFormatter: Intl.DateTimeFormat | null = null

function getHourFormatter(): Intl.DateTimeFormat {
  const tz = resolveTimezone()
  if (!cachedHourFormatter || cachedHourTz !== tz) {
    cachedHourTz = tz
    cachedHourFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      hourCycle: 'h23',
    })
  }
  return cachedHourFormatter
}

/**
 * Wall-clock hour (0–23) in the dining timezone at `now`. THE way to make
 * an hour-based decision (meal period, cutoffs) on the server — Vercel runs
 * in UTC, so `new Date().getHours()` there is the UTC hour, which after
 * 5pm PT is tomorrow's hour 0–6 (ship-audit P0-1). In a facility-local
 * browser this returns the same value as `getHours()`.
 */
export function getDiningHour(now: Date = new Date()): number {
  return Number(getHourFormatter().format(now))
}

let cachedPartsTz: string | null = null
let cachedPartsFormatter: Intl.DateTimeFormat | null = null

function getHourMinuteFormatter(): Intl.DateTimeFormat {
  const tz = resolveTimezone()
  if (!cachedPartsFormatter || cachedPartsTz !== tz) {
    cachedPartsTz = tz
    cachedPartsFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    })
  }
  return cachedPartsFormatter
}

/**
 * Wall-clock { hour, minute } in the dining timezone.
 * Use for cutoffs that need minute precision (e.g. lunch auto-close 13:20).
 */
export function getDiningHourMinute(
  now: Date = new Date(),
): { hour: number; minute: number } {
  const parts = getHourMinuteFormatter().formatToParts(now)
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0)
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0)
  return { hour, minute }
}

/**
 * Offset of `tz` from UTC, in ms, AT the given instant (DST-aware).
 * Positive when the zone is behind UTC (e.g. America/Vancouver = +7h in
 * PDT). Computed with formatToParts so there is no Date-string parsing.
 */
function tzOffsetMs(at: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  })
  const p: Record<string, number> = {}
  for (const part of dtf.formatToParts(at)) {
    if (part.type !== 'literal') p[part.type] = Number(part.value)
  }
  // fractionalSecond must be included or sub-second bounds (…T23:59:59.999)
  // drift by up to ~1s when the offset is reconstructed.
  const wallAsUTC = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
    p.fractionalSecond ?? 0,
  )
  return wallAsUTC - at.getTime()
}

/**
 * Convert a dining-local wall-clock time on a given dining day to the
 * exact UTC instant, as an ISO string. DST-correct (re-evaluates the
 * offset at the resolved instant so the spring-forward / fall-back hour
 * lands right). This is the ONLY correct way to build a UTC query bound
 * for a dining-local window — never stamp a local wall time with `Z`.
 */
function diningLocalToUtcISO(dateStr: string, timeStr: string): string {
  const tz = resolveTimezone()
  const guess = new Date(`${dateStr}T${timeStr}Z`)
  const offset = tzOffsetMs(guess, tz)
  let real = new Date(guess.getTime() - offset)
  const offset2 = tzOffsetMs(real, tz)
  if (offset2 !== offset) real = new Date(guess.getTime() - offset2)
  return real.toISOString()
}

/**
 * UTC [start, end] ISO instants spanning a full dining-local calendar
 * day (00:00:00.000 → 23:59:59.999 in the dining timezone). Use for
 * `Query.between` / day-range filtering of `datetime` columns and
 * `$createdAt` so a late-evening order is bucketed into the correct
 * dining day instead of leaking across the UTC midnight boundary.
 */
export function getDiningDayWindowUTC(
  dateStr: string = getMealDateToday(),
): { start: string; end: string } {
  return {
    start: diningLocalToUtcISO(dateStr, '00:00:00.000'),
    end: diningLocalToUtcISO(dateStr, '23:59:59.999'),
  }
}

/**
 * True when a stored **instant** falls on the given dining day.
 *
 * Use this for any `datetime` column written as a true instant
 * (`new Date().toISOString()`) that you want to bucket by dining day —
 * `last_login_at`, `timestamp`, `$createdAt`.
 *
 * **Never compare such a column with `String.startsWith(diningDay)`.** The
 * stored value carries a UTC date prefix while the dining day is local, and
 * the two disagree for precisely the hours that matter: from ~17:00 PT — all
 * of dinner service — the UTC date is already tomorrow. That mismatch marked
 * the dinner crew as "not logged in today" on the team lead's Close Dining
 * Room screen, and simultaneously counted *yesterday's* dinner logins as
 * today's. Both directions were wrong, and neither raised an error.
 *
 * Returns `false` for null/empty/unparseable input rather than throwing —
 * callers are render paths, and a bad timestamp must not blank the screen.
 */
export function isOnDiningDay(
  instant: string | Date | null | undefined,
  dateStr: string = getMealDateToday(),
): boolean {
  if (!instant) return false
  const at = instant instanceof Date ? instant : new Date(instant)
  if (Number.isNaN(at.getTime())) return false
  return getMealDate(at) === dateStr
}

/**
 * Milliseconds from `now` until the next dining-day boundary — the instant
 * the dining-local calendar rolls over to the next day (local midnight).
 *
 * Used by long-lived realtime subscriptions to re-key their `meal_date`
 * filter at midnight: a tablet left on overnight otherwise keeps filtering
 * yesterday's dining day and silently misses the new day's events (masked
 * only by the 15–30s poll fallback). Schedule a `setTimeout` with this delay;
 * when it fires, refresh the meal-date value and reschedule.
 *
 * DST-correct — the boundary is derived from `getDiningDayWindowUTC` (which
 * re-evaluates the zone offset at the resolved instant). Always returns a
 * value `>= 1` so it is safe to pass straight to `setTimeout` without a
 * same-tick reschedule loop.
 */
export function msUntilNextDiningDayBoundary(now: Date = new Date()): number {
  // End of the dining day containing `now`, as a UTC instant (…T23:59:59.999
  // dining-local). The next boundary is one millisecond later.
  const endOfTodayMs = new Date(getDiningDayWindowUTC(getMealDate(now)).end).getTime()
  const nextBoundaryMs = endOfTodayMs + 1
  const delta = nextBoundaryMs - now.getTime()
  return delta > 0 ? delta : 1
}

/**
 * UTC [start, end] ISO instants for an AM / PM cash-out shift, expressed
 * in dining-local wall time then converted to UTC. AM = 07:00 → 15:00
 * local, PM = 15:00 → 22:00 local (bounds preserved from the prior
 * cash-out semantics; the bug was stamping these local times with `Z`).
 */
export function getDiningShiftWindowUTC(
  shift: 'am' | 'pm',
  dateStr: string = getMealDateToday(),
): { start: string; end: string } {
  return shift === 'am'
    ? {
        start: diningLocalToUtcISO(dateStr, '07:00:00.000'),
        end: diningLocalToUtcISO(dateStr, '14:59:59.999'),
      }
    : {
        start: diningLocalToUtcISO(dateStr, '15:00:00.000'),
        end: diningLocalToUtcISO(dateStr, '21:59:59.999'),
      }
}

/**
 * UTC [start, end] ISO bounds spanning a full UTC CALENDAR day for a picked
 * date string (YYYY-MM-DD).
 *
 * Use this — NOT `getDiningDayWindowUTC` — for columns that hold a user-picked
 * calendar DATE written date-only into an Appwrite `datetime` column (e.g.
 * `reservations.reservation_date`). Such a write is stored at UTC midnight
 * (`2026-06-15` → `2026-06-15T00:00:00.000+00:00`), and `Query.equal` against
 * the date-only string never matches the stored timestamp (see ADR 0007). A
 * UTC-day range matches both a date-only (UTC-midnight) write and any legacy
 * row that carries a wall-clock time on that UTC date.
 *
 * The dining-day window is deliberately NOT used here: it is anchored to
 * America/Vancouver, so its start (07:00Z in PDT) would fall AFTER a
 * UTC-midnight write and skip it. Dining-day windows are for event TIMESTAMPS
 * (`check_date`, `$createdAt`); this is for a picked calendar DATE.
 */
export function getUtcCalendarDayRange(dateStr: string): { start: string; end: string } {
  return {
    start: `${dateStr}T00:00:00.000+00:00`,
    end: `${dateStr}T23:59:59.999+00:00`,
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Occurred-at vs recorded-at
//
// "Never trust the client's wallclock" is the right rule for an ONLINE write:
// the server's clock is authoritative and the two are seconds apart. It is the
// WRONG rule on the offline-replay path, where the client instant is the only
// record of when the business event actually happened — deriving the dining
// day from `now` there attributes Friday's dinner to whatever day the tablet
// happened to reconnect on.
//
// The fix is not to trust the client, and not to ignore it: accept the instant
// as DATA, bound it, and keep deriving the dining day server-side so the
// timezone logic stays in exactly one place (this file).
//
// Bounds are deliberately ASYMMETRIC. A clock reporting the past is ordinary —
// that is what a queue is. A clock reporting the future is always skew.
// ───────────────────────────────────────────────────────────────────────────

/** A client instant further ahead than this is skew, never a queued order. */
export const MAX_OCCURRED_AT_FUTURE_SKEW_MS = 5 * 60 * 1000

/** Older than this and we can no longer attribute it confidently (48 h covers a long-weekend outage). */
export const MAX_OCCURRED_AT_PAST_AGE_MS = 48 * 60 * 60 * 1000

export type BusinessInstantSource = 'occurred_at' | 'server_now'

/** Why a supplied instant was refused; null when it was accepted. */
export type BusinessInstantRejection =
  | 'absent'
  | 'unparseable'
  | 'future_skew'
  | 'too_old'
  | null

export interface BusinessInstantResolution {
  /** The instant the business event is attributed to. Never an Invalid Date. */
  instant: Date
  /** Dining day for `instant`, via the same helper every other caller uses. */
  mealDate: string
  source: BusinessInstantSource
  rejectedReason: BusinessInstantRejection
  /** `occurredAt - now` in ms; negative = client behind. Null when unusable. Log it to find a drifting tablet. */
  skewMs: number | null
}

/**
 * Resolve which instant a write should be attributed to.
 *
 * Absent (the online path) → server `now`, so existing behaviour is byte for
 * byte unchanged. Present and plausible → the client instant. Present and
 * implausible → server `now`, with `rejectedReason` set so the caller can log
 * a drifting device instead of silently writing its bad data.
 */
export function resolveBusinessInstant(
  occurredAtIso: string | null | undefined,
  now: Date = new Date(),
): BusinessInstantResolution {
  const fallback = (
    rejectedReason: BusinessInstantRejection,
    skewMs: number | null,
  ): BusinessInstantResolution => ({
    instant: now,
    mealDate: getMealDate(now),
    source: 'server_now',
    rejectedReason,
    skewMs,
  })

  if (occurredAtIso == null || occurredAtIso === '') return fallback('absent', null)

  const parsed = new Date(occurredAtIso)
  if (Number.isNaN(parsed.getTime())) return fallback('unparseable', null)

  const skewMs = parsed.getTime() - now.getTime()
  if (skewMs > MAX_OCCURRED_AT_FUTURE_SKEW_MS) return fallback('future_skew', skewMs)
  if (-skewMs > MAX_OCCURRED_AT_PAST_AGE_MS) return fallback('too_old', skewMs)

  return {
    instant: parsed,
    mealDate: getMealDate(parsed),
    source: 'occurred_at',
    rejectedReason: null,
    skewMs,
  }
}
